/**
 * WidgetRenderer Component (Story 5.4 → updated Story 5.5, 5.7)
 *
 * Dispatches widget rendering to the appropriate type-specific component.
 * Passes optional data prop and wraps each widget in an error boundary.
 * Passes onAutoResize callback to widgets that support auto-sizing.
 */

import type { WidgetConfig } from "../../../lib/templateUtils";
import type { WidgetData } from "../../../lib/widgetData";
import { WidgetAccountContributionContext } from "./chartWidgetShell";
import { WidgetErrorBoundary } from "./WidgetErrorBoundary";
import { KpiWidget } from "./KpiWidget";
import { LineChartWidget } from "./LineChartWidget";
import { BarChartWidget } from "./BarChartWidget";
import { PieChartWidget } from "./PieChartWidget";
import { DataTableWidget } from "./DataTableWidget";
import { TextBlockWidget } from "./TextBlockWidget";
import { TopPostsWidget } from "./TopPostsWidget";
import { CoverPageWidget } from "./CoverPageWidget";
import { FunnelWidget } from "./FunnelWidget";
import { DemographicWidget } from "./DemographicWidget";
import { HeadingWidget } from "./HeadingWidget";
import { ParagraphWidget } from "./ParagraphWidget";
import { RichTextWidget } from "./RichTextWidget";
import { SpacerDividerWidget } from "./SpacerDividerWidget";
import { ImageWidget } from "./ImageWidget";
import { EmbedWidget } from "./EmbedWidget";
import { CustomMetricWidget } from "./CustomMetricWidget";
import { BenchmarkWidget } from "./BenchmarkWidget";
import { GoalWidget } from "./GoalWidget";
import { GaugeWidget } from "./GaugeWidget";
import { RollupTableWidget } from "./RollupTableWidget";

export interface WidgetRendererProps {
  widget: WidgetConfig;
  widgetData?: Record<string, WidgetData>;
  onAutoResize?: (widgetId: string, newRowSpan: number) => void;
  /** Cover page: date range start for period display */
  reportDateRangeStart?: string;
  /** Cover page: date range end for period display */
  reportDateRangeEnd?: string;
  /** Cover page: client name (derived, not stored in config) */
  selectedClientName?: string;
  /** Cover page: client logo URL from client profile (auto-pull) */
  selectedClientLogoUrl?: string;
  /** Report format — controls cover page badge text (Story 5.23) */
  format?: "document" | "slide-deck";
  /** Embed widget: when true, renders live iframe instead of static placeholder */
  isPreviewMode?: boolean;
}

import type { WidgetDataEnvelope, AnyWidgetPayload } from "../../../lib/widgetData";

type WidgetComponent = React.ComponentType<{
  widget: WidgetConfig;
  data?: WidgetDataEnvelope<AnyWidgetPayload>;
  onAutoResize?: (widgetId: string, newRowSpan: number) => void;
}>;

// Exported so the structural-parity matrix (Story 37.9, WidgetPreview.parity.test.tsx)
// can assert its row set EQUALS this registry — a future widget type added here
// without a matrix row then fails CI (no silent coverage gap). The dispatch
// authority is this map, so the matrix must track it, not a hardcoded count.
export const WIDGET_COMPONENTS: Record<string, WidgetComponent> = {
  kpi: KpiWidget as WidgetComponent,
  "line-chart": LineChartWidget as WidgetComponent,
  "bar-chart": BarChartWidget as WidgetComponent,
  "pie-chart": PieChartWidget as WidgetComponent,
  "data-table": DataTableWidget as WidgetComponent,
  "text-block": TextBlockWidget as WidgetComponent,
  "top-posts": TopPostsWidget as WidgetComponent,
  "cover-page": CoverPageWidget as WidgetComponent,
  funnel: FunnelWidget as WidgetComponent,
  demographic: DemographicWidget as WidgetComponent,
  heading: HeadingWidget as WidgetComponent,
  paragraph: ParagraphWidget as WidgetComponent,
  "rich-text": RichTextWidget as WidgetComponent,
  "spacer-divider": SpacerDividerWidget as WidgetComponent,
  image: ImageWidget as WidgetComponent,
  embed: EmbedWidget as WidgetComponent,
  "custom-metric": CustomMetricWidget as WidgetComponent,
  benchmark: BenchmarkWidget as WidgetComponent,
  goal: GoalWidget as WidgetComponent,
  gauge: GaugeWidget as WidgetComponent,
  "rollup-table": RollupTableWidget as WidgetComponent,
};

export function WidgetRenderer({
  widget,
  widgetData,
  onAutoResize,
  reportDateRangeStart,
  reportDateRangeEnd,
  selectedClientName,
  selectedClientLogoUrl,
  format,
  isPreviewMode,
}: WidgetRendererProps) {
  const Component = WIDGET_COMPONENTS[widget.type];

  if (!Component) {
    return (
      <div data-testid={`widget-${widget.id}`} className="h-full flex flex-col p-3">
        <div data-testid="widget-type-unknown" className="flex-1 flex items-center justify-center">
          <span className="text-xs text-muted-foreground">
            Unknown widget type: {widget.type}
          </span>
        </div>
      </div>
    );
  }

  const data = widgetData?.[widget.id];
  const isLoading = data?.loading ?? false;

  // Cover page receives additional props for derived data (date range, client info)
  if (widget.type === "cover-page") {
    return (
      <div data-testid={`widget-${widget.id}`} className="h-full" aria-busy={isLoading}>
        <WidgetErrorBoundary widgetTitle={widget.title}>
          <CoverPageWidget
            widget={widget}
            reportDateRangeStart={reportDateRangeStart}
            reportDateRangeEnd={reportDateRangeEnd}
            selectedClientName={selectedClientName}
            selectedClientLogoUrl={selectedClientLogoUrl}
            format={format}
          />
        </WidgetErrorBoundary>
      </div>
    );
  }

  // Benchmark widget receives isPreviewMode + data for preview vs edit mode.
  // Story 32-7: wrapped in the contribution provider like the generic branch
  // (the server never attaches the field to benchmark envelopes today — the
  // benchmark branch bypasses recordsForWidget — but the provider keeps the
  // dispatch symmetric for when benchmark ever honors selections).
  if (widget.type === "benchmark") {
    return (
      <div data-testid={`widget-${widget.id}`} className="h-full" aria-busy={isLoading}>
        <WidgetAccountContributionContext.Provider value={data?.accountContribution}>
          <WidgetErrorBoundary widgetTitle={widget.title}>
            <BenchmarkWidget widget={widget} data={data} isPreviewMode={isPreviewMode} />
          </WidgetErrorBoundary>
        </WidgetAccountContributionContext.Provider>
      </div>
    );
  }

  // Embed widget receives isPreviewMode to toggle static placeholder vs live iframe
  if (widget.type === "embed") {
    return (
      <div data-testid={`widget-${widget.id}`} className="h-full" aria-busy={isLoading}>
        <WidgetErrorBoundary widgetTitle={widget.title}>
          <EmbedWidget widget={widget} isPreviewMode={isPreviewMode} />
        </WidgetErrorBoundary>
      </div>
    );
  }

  // Story 32-7 — ONE dispatch point delivers the fetch-time account-
  // contribution metadata to every shell-using data widget (zero per-widget
  // edits; the WIDGET_COMPONENTS registry is untouched). Static-content
  // envelopes never carry the field (server attach rule) so static widgets
  // simply provide `undefined`.
  return (
    <div data-testid={`widget-${widget.id}`} className="h-full" aria-busy={isLoading}>
      <WidgetAccountContributionContext.Provider value={data?.accountContribution}>
        <WidgetErrorBoundary widgetTitle={widget.title}>
          <Component widget={widget} data={data} onAutoResize={onAutoResize} />
        </WidgetErrorBoundary>
      </WidgetAccountContributionContext.Provider>
    </div>
  );
}
